import { createSelector } from 'reselect';
import { EDUCATOR_ROLE, STUDENT_ROLE } from '@core/constants/space';
import {
  accessingSpaceAsStaff,
  isInstructorInSpace,
} from '@core/selectors/classroom';
import { getAllSpaces } from '@core/selectors/space/space.selectors';
import { currentUser, currentUserId } from '@core/selectors/user';
import { AulaState, Space } from '@core/types/state';
import { HomeViewRole } from '@app/constants/home';

export const getHomeState = (state: AulaState) => state.home;

const getUserRoleInSpace = (
  state: AulaState,
  spaceId: string,
  userId: string | null
): HomeViewRole => {
  if (!userId) {
    return STUDENT_ROLE;
  }

  if (
    accessingSpaceAsStaff(state)(spaceId) ||
    isInstructorInSpace(state)(userId, spaceId)
  ) {
    return EDUCATOR_ROLE;
  }

  return STUDENT_ROLE;
};

export const getDefaultHomeViewRole = (state: AulaState): HomeViewRole => {
  const userId = currentUserId(state);
  const spaces = Object.values(getAllSpaces(state) || {});

  const hasEducatorSpace = spaces.some(
    (space) =>
      getUserRoleInSpace(state, space.objectId, userId) === EDUCATOR_ROLE
  );

  return hasEducatorSpace ? EDUCATOR_ROLE : STUDENT_ROLE;
};

export const getHomeViewRole = (state: AulaState): HomeViewRole =>
  getHomeState(state)?.viewRole ?? getDefaultHomeViewRole(state);

export const getCanSwitchHomeViewRole = createSelector(
  [(state: AulaState) => state, getAllSpaces, currentUserId],
  (state, spaces, userId) => {
    if (!userId) {
      return false;
    }

    let hasEducatorSpace = false;
    let hasStudentSpace = false;

    Object.values(spaces || {}).forEach((space) => {
      if (getUserRoleInSpace(state, space.objectId, userId) === EDUCATOR_ROLE) {
        hasEducatorSpace = true;
      } else {
        hasStudentSpace = true;
      }
    });

    return hasEducatorSpace && hasStudentSpace;
  }
);

export const getHomeSpacesForView = createSelector(
  [(state: AulaState) => state, getHomeViewRole],
  (state, viewRole): Space[] => {
    const userId = currentUserId(state);
    return Object.values(getAllSpaces(state) || {})
      .filter((space) => !space.archived)
      .filter(
        (space) =>
          getUserRoleInSpace(state, space.objectId, userId) === viewRole
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }
);

export const getHomeUserFirstName = (state: AulaState): string =>
  currentUser(state)?.firstName || '';

export const getHomeUserPreferences = createSelector(
  [(state: AulaState) => state],
  (state) => [
    {
      id: 'widget-000',
      isHidden: false,
    },
    {
      id: 'widget-001',
      isHidden: false,
    },
  ]
);
